import { Heading, Paragraph } from '@zillow/constellation';
import { InlineDd, InlineDt } from '../../BackgroundReportEvictions/EvictionSectionContent';
import { getTitleCase } from '../../../../utils/formatters';
import { CriminalOffense } from './CriminalOffense';
import type { CriminalRecord } from '@zg-rentals/ts-capps-client';

export function CriminalSectionContent({ record }: { record: CriminalRecord }) {
  const { subject, offenses } = record;
  const {
    fullName,
    alias,
    dob,
    sex,
    hairColor,
    eyeColor,
    weight,
    height,
    skinTone,
    militaryService,
    age,
    ageAtDeath,
    deathVerificationCode,
    scarsMarks,
    address,
    caseNumber,
    state,
    category,
    sourceOfRecord,
    jurisdiction,
    comments,
  } = subject;

  return (
    <>
      {(fullName ||
        alias ||
        dob ||
        sex ||
        hairColor ||
        eyeColor ||
        weight ||
        height ||
        skinTone ||
        militaryService ||
        age ||
        ageAtDeath ||
        deathVerificationCode ||
        scarsMarks ||
        address) && (
        <>
          <Heading level={5} fontColor="coolGray500" fontType="finePrintHeading" marginBottom="xs">
            Subject information
          </Heading>
          <dl>
            {fullName && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Full name:</InlineDt> <InlineDd>{fullName}</InlineDd>
              </Paragraph>
            )}
            {alias && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Alias:</InlineDt> <InlineDd>{alias}</InlineDd>
              </Paragraph>
            )}
            {dob && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Date of birth:</InlineDt> <InlineDd>{dob}</InlineDd>
              </Paragraph>
            )}
            {sex && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Sex:</InlineDt> <InlineDd>{getTitleCase(sex)}</InlineDd>
              </Paragraph>
            )}
            {hairColor && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Hair color:</InlineDt> <InlineDd>{getTitleCase(hairColor)}</InlineDd>
              </Paragraph>
            )}
            {eyeColor && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Eye color:</InlineDt> <InlineDd>{getTitleCase(eyeColor)}</InlineDd>
              </Paragraph>
            )}
            {weight && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Weight:</InlineDt> <InlineDd>{weight.toLowerCase()}</InlineDd>
              </Paragraph>
            )}
            {height && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Height:</InlineDt> <InlineDd>{height.toLowerCase()}</InlineDd>
              </Paragraph>
            )}
            {skinTone && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Skin tone:</InlineDt> <InlineDd>{getTitleCase(skinTone)}</InlineDd>
              </Paragraph>
            )}
            {militaryService && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Military service:</InlineDt> <InlineDd>{getTitleCase(militaryService)}</InlineDd>
              </Paragraph>
            )}
            {age && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Age:</InlineDt> <InlineDd>{age}</InlineDd>
              </Paragraph>
            )}
            {ageAtDeath && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Age at death:</InlineDt> <InlineDd>{ageAtDeath}</InlineDd>
              </Paragraph>
            )}
            {deathVerificationCode && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Death verification code:</InlineDt> <InlineDd>{deathVerificationCode}</InlineDd>
              </Paragraph>
            )}
            {scarsMarks && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Scars/Marks:</InlineDt> <InlineDd>{getTitleCase(scarsMarks)}</InlineDd>
              </Paragraph>
            )}
            {address && (
              <Paragraph fontType="bodySmall">
                <InlineDt>Address:</InlineDt> <InlineDd>{address}</InlineDd>
              </Paragraph>
            )}
          </dl>

          {(caseNumber || state || category || sourceOfRecord || jurisdiction || comments) && (
            <>
              <Heading level={5} fontColor="coolGray500" fontType="finePrintHeading" marginTop="sm" marginBottom="xs">
                Case details
              </Heading>
              {caseNumber && (
                <Paragraph fontType="bodySmall">
                  <InlineDt>Case number:</InlineDt> <InlineDd>{caseNumber}</InlineDd>
                </Paragraph>
              )}
              {state && (
                <Paragraph fontType="bodySmall">
                  <InlineDt>State:</InlineDt> <InlineDd>{state}</InlineDd>
                </Paragraph>
              )}
              {category && (
                <Paragraph fontType="bodySmall">
                  <InlineDt>Category:</InlineDt> <InlineDd>{getTitleCase(category)}</InlineDd>
                </Paragraph>
              )}
              {sourceOfRecord && (
                <Paragraph fontType="bodySmall">
                  <InlineDt>Source:</InlineDt> <InlineDd>{sourceOfRecord}</InlineDd>
                </Paragraph>
              )}
              {jurisdiction && (
                <Paragraph fontType="bodySmall">
                  <InlineDt>Jurisdiction:</InlineDt> <InlineDd>{jurisdiction}</InlineDd>
                </Paragraph>
              )}
              {comments && (
                <Paragraph fontType="bodySmall">
                  <InlineDt>Comments:</InlineDt> <InlineDd>{comments}</InlineDd>
                </Paragraph>
              )}
            </>
          )}

          {offenses.map((offense, index) => (
            <CriminalOffense offense={offense} key={index} />
          ))}
        </>
      )}
    </>
  );
}
